import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAppAnalytics } from "../utils/useAppAnalytics";
import DrWeedyLogo from "../assets/DrWeedyLogo";

export default function LoginPage() {
  const { login, loading, error: authError, user } = useAuth();
  const { trackEvent, identifyUser } = useAppAnalytics({ user });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    const success = await login(email, password);
    if (success) {
      await identifyUser();
      await trackEvent("login_success", { method: "email" });
    } else {
      const errorMsg = authError || "Erro ao fazer login";
      setError(errorMsg);
      await trackEvent("login_failed", { method: "email", error_message: errorMsg });
    }
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-saluton/15 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-petrolio/10 via-transparent to-transparent"></div>

      <div className="hidden md:flex flex-1 items-center justify-center relative z-10">
        <div className="text-center max-w-md">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-saluton/20 via-petrolio/20 to-saluton/20 rounded-3xl blur-3xl"></div>
            <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl p-16 border border-white/20 shadow-2xl">
              <div className="w-28 h-28 mx-auto mb-8 flex items-center justify-center">
                <DrWeedyLogo width={112} height={112} />
              </div>
              <Typography
                variant="h3"
                sx={{
                  color: "#ffffff",
                  fontWeight: 700,
                  marginBottom: "16px",
                  letterSpacing: "-0.02em",
                  fontFamily: "Oxanium, sans-serif",
                }}
              >
                Dr. Weedy
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                Assistente com inteligência artificial para profissionais da
                saúde. Orientações precisas e confiáveis, disponíveis 24/7.
              </Typography>
              <div className="mt-8 flex flex-col items-center gap-3">
                <span className="text-white/50 text-xs font-medium tracking-wider uppercase">Powered by</span>
                <img src="/saluton-dark-horizontal.png" alt="Saluton Telemed" className="h-28 w-auto" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full md:w-[520px] flex flex-col justify-center px-12 py-16 relative z-10">
        <div className="max-w-md mx-auto w-full">
          <div className="text-center mb-10">
            <Typography
              variant="h2"
              sx={{
                color: "#ffffff",
                fontWeight: 700,
                marginBottom: "12px",
                letterSpacing: "-0.03em",
                fontFamily: "Oxanium, sans-serif",
              }}
            >
              Bem-vindo de volta
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              Acesse sua conta para continuar suas pesquisas
            </Typography>
          </div>
          <Paper
            elevation={8}
            sx={{
              background:
                "linear-gradient(135deg, rgba(26, 49, 60, 0.8) 0%, rgba(4, 9, 10, 0.9) 100%)",
              backdropFilter: "blur(20px)",
              border: "2px solid rgba(226, 114, 91, 0.2)",
              borderRadius: "32px",
              padding: "40px",
              boxShadow:
                "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(226, 114, 91, 0.1)",
            }}
          >
            <Box component="form" onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    borderRadius: "20px",
                    background: "rgba(225, 100, 100, 0.15)",
                    border: "1px solid rgba(225, 100, 100, 0.3)",
                    color: "#e16464",
                    fontWeight: 500,
                  }}
                >
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Email"
                type="email"
                data-test="email-login"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "20px",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    color: "#ffffff",
                    fontSize: "17px",
                    "& fieldset": {
                      borderColor: "rgba(255,255,255,0.15)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(226, 114, 91, 0.4)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#E2725B",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "16px",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#E2725B",
                  },
                  "& .MuiInputLabel-shrink": {
                    backgroundColor: "#0f2a35",
                    padding: "0 8px",
                    borderRadius: "4px",
                  },
                }}
              />

              <TextField
                fullWidth
                label="Senha"
                type="password"
                data-test="senha-login"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "20px",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    color: "#ffffff",
                    fontSize: "17px",
                    "& fieldset": {
                      borderColor: "rgba(255,255,255,0.15)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(226, 114, 91, 0.4)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#E2725B",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "16px",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#E2725B",
                  },
                  "& .MuiInputLabel-shrink": {
                    backgroundColor: "#0f2a35",
                    padding: "0 8px",
                    borderRadius: "4px",
                  },
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    sx={{
                      color: "rgba(255,255,255,0.4)",
                      "&.Mui-checked": {
                        color: "#E2725B",
                      },
                      "&:hover": {
                        backgroundColor: "rgba(226, 114, 91, 0.1)",
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      fontSize: "15px",
                      fontWeight: 500,
                    }}
                  >
                    Lembrar-me neste dispositivo
                  </Typography>
                }
              />

              <Box sx={{ textAlign: "right", mt: -1 }}>
                <Link
                  to="/forgot"
                  style={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "14px",
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#E2725B")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.6)")}
                >
                  Esqueceu a senha?
                </Link>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                data-test="login"
                disabled={loading}
                sx={{
                  background:
                    "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
                  borderRadius: "24px",
                  textTransform: "none",
                  fontSize: "18px",
                  fontWeight: 700,
                  padding: "18px 32px",
                  color: "#ffffff",
                  border: "2px solid rgba(226, 114, 91, 0.3)",
                  boxShadow: "0 8px 24px rgba(226, 114, 91, 0.4)",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 32px rgba(226, 114, 91, 0.5)",
                    background:
                      "linear-gradient(135deg, #CC3B2A 0%, #E2725B 100%)",
                  },
                  "&:disabled": {
                    background: "rgba(255,255,255,0.1)",
                    color: "rgba(255,255,255,0.4)",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={28} sx={{ color: "#ffffff" }} />
                ) : (
                  "Entrar na Pesquisa"
                )}
              </Button>
            </Box>
          </Paper>

          <div className="text-center mt-8">
            <Typography
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "15px",
                marginBottom: "8px",
              }}
            >
              Ainda não tem conta?
            </Typography>
            <Link
              to="/signup"
              style={{
                color: "#E2725B",
                fontSize: "16px",
                fontWeight: 700,
                textDecoration: "none",
                transition: "opacity 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              Criar conta
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
